import { Moon, Activity, Heart, Brain, BookOpen, Sparkles, ShieldCheck, AlertTriangle, TrendingUp } from "lucide-react";

const ICON_BY_KEY = {
  moon: Moon,
  activity: Activity,
  heart: Heart,
  brain: Brain,
  "book-open": BookOpen,
  sparkles: Sparkles,
};

const CATEGORY_META = {
  protective: { label: "Hábito protector", chipIcon: ShieldCheck, chipText: "Protector" },
  risk: { label: "A revisar", chipIcon: AlertTriangle, chipText: "A revisar" },
  evolution: { label: "Evolución", chipIcon: TrendingUp, chipText: "Evolución" },
  neutral: { label: "Patrón a observar", chipIcon: Sparkles, chipText: "Observación" },
};

const TONE_STYLES = {
  positive: {
    surface: "bg-gradient-to-br from-sage-50/80 via-sage-50/40 to-cream-50/60",
    border: "border-sage-200/60",
    iconWrap: "bg-sage-100/80 text-sage-700",
    accent: "text-sage-700",
    chipBg: "bg-sage-100/80 text-sage-700",
    confidenceBg: "bg-sage-500/15 text-sage-700",
  },
  risk: {
    surface: "bg-gradient-to-br from-[#FBEDE5]/80 via-[#FAE5DA]/40 to-cream-50/60",
    border: "border-coral-100",
    iconWrap: "bg-coral-100/80 text-coral-500",
    accent: "text-coral-500",
    chipBg: "bg-coral-100/80 text-coral-500",
    confidenceBg: "bg-coral-500/15 text-coral-500",
  },
  neutral: {
    surface: "bg-gradient-to-br from-cream-50/80 via-beige-100/40 to-cream-50/60",
    border: "border-sand-200/70",
    iconWrap: "bg-beige-100/80 text-ink-600",
    accent: "text-ink-600",
    chipBg: "bg-beige-100/80 text-ink-600",
    confidenceBg: "bg-ink-900/8 text-ink-600",
  },
};

const CONFIDENCE_RANK = {
  "Alta confianza": 3,
  "Confianza moderada": 2,
  "Confianza inicial": 1,
  "Poca evidencia": 0,
};

export default function InsightsList({
  items = [],
  emptyHint,
  message,
  learning,
  variant = "full",
}) {
  if (learning) {
    return (
      <div className="tk-soft-card text-center" data-testid="insights-learning">
        <p className="font-outfit text-lg text-ink-900">Aún estamos aprendiendo sobre tus patrones</p>
        <p className="font-dmsans text-sm text-ink-600 mt-1 max-w-md mx-auto">
          {message || "Necesitamos más registros para generar conclusiones confiables."}
        </p>
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="tk-soft-card text-center" data-testid="insights-empty">
        <p className="font-outfit text-lg text-ink-900">Sin patrones relevantes todavía</p>
        <p className="font-dmsans text-sm text-ink-600 mt-1 max-w-md mx-auto">
          {emptyHint || "Sumá más registros en este período para que podamos analizar tus patrones."}
        </p>
      </div>
    );
  }
  const gridClass = variant === "compact"
    ? "grid grid-cols-1 gap-3"
    : "grid grid-cols-1 md:grid-cols-2 gap-3";

  return (
    <div className={gridClass} data-testid="insights-list">
      {items.map((it) => (
        <InsightCard key={it.id} insight={it} variant={variant} />
      ))}
    </div>
  );
}

function InsightCard({ insight, variant }) {
  const Icon = ICON_BY_KEY[insight.icon] || Sparkles;
  const tone = TONE_STYLES[insight.tone] || TONE_STYLES.neutral;
  const cat = CATEGORY_META[insight.category] || CATEGORY_META.neutral;
  const ChipIcon = cat.chipIcon;
  const showExpand = variant !== "compact";

  return (
    <article
      className={`rounded-3xl border ${tone.border} ${tone.surface} backdrop-blur-md p-5 transition hover:shadow-[0_18px_50px_-28px_rgba(83,109,83,0.25)]`}
      data-testid={`insight-${insight.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-2xl flex items-center justify-center shrink-0 ${tone.iconWrap}`}>
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="font-outfit text-base text-ink-900 leading-snug">{insight.title}</p>
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-dmsans whitespace-nowrap ${tone.chipBg}`}
              data-testid={`insight-${insight.id}-category`}
            >
              <ChipIcon size={11} />
              {cat.chipText}
            </span>
          </div>
          <p className="font-dmsans text-sm text-ink-900 mt-2 leading-relaxed">{insight.narrative}</p>

          {showExpand && (insight.meaning || insight.why) && (
            <div className="mt-3 space-y-2 text-sm font-dmsans">
              {insight.what && (
                <p className="text-ink-600 leading-relaxed">
                  <span className="tk-label text-[10px] mr-1.5">Qué observamos</span>
                  {insight.what}
                </p>
              )}
              {insight.meaning && (
                <p className="text-ink-900 leading-relaxed">
                  <span className="tk-label text-[10px] mr-1.5">Qué significa</span>
                  {insight.meaning}
                </p>
              )}
              {insight.why && (
                <p className="text-ink-600 leading-relaxed">
                  <span className="tk-label text-[10px] mr-1.5">Por qué</span>
                  {insight.why}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 mt-3">
            {insight.strength && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-ink-900/5 text-ink-600 text-[10px] font-dmsans">
                {insight.strength}
              </span>
            )}
            {insight.confidence && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-dmsans ${tone.confidenceBg}`}
                data-testid={`insight-${insight.id}-confidence`}
              >
                {insight.confidence}
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

export { CONFIDENCE_RANK };
